"use client";

import { getContentIcon } from "@/lib/icons";
import { AnimatedSection } from "@/components/ui/animated-section";

interface Feature {
 title: string;
 description?: string;
 icon?: string;
}

interface FeatureGridProps {
 title?: string;
 subtitle?: string;
 features: Feature[];
 columns?: 2 | 3 | 4;
 variant?: "card" | "simple" | "icon";
}

const gridCols = {
 2: "md:grid-cols-2",
 3: "md:grid-cols-2 lg:grid-cols-3",
 4: "md:grid-cols-2 lg:grid-cols-4",
};

function getFeatureFallbackDescription(title: string, description?: string) {
  const cleaned = (description || "").trim();
  if (cleaned.length >= 36) return cleaned;

  const key = title.toLowerCase();
  if (/coupon|offer|promotion/.test(key)) {
    return "Run targeted offer experiences with urgency and clear conversion attribution.";
  }
  if (/countdown|timer|counter/.test(key)) {
    return "Add urgency moments that guide high-intent visitors toward faster decisions.";
  }
  if (/email|lead|collector|form/.test(key)) {
    return "Capture quality leads using contextual prompts and conversion-focused journeys.";
  }
  if (/social|proof|review|testimonial/.test(key)) {
    return "Build visitor trust with real-time social proof signals and credibility cues.";
  }
  return `${title} helps improve visitor engagement and keeps conversion flow focused across critical touchpoints.`;
}

function getFeatureTag(title: string) {
  const key = title.toLowerCase();
  if (/coupon|offer|promotion|countdown|timer/.test(key)) return "Revenue";
  if (/social|proof|review|testimonial/.test(key)) return "Trust";
  if (/email|lead|collector|form/.test(key)) return "Lead Gen";
  if (/cookie|consent|privacy/.test(key)) return "Compliance";
  return "Optimization";
}

export function FeatureGrid({
 title,
 subtitle,
 features,
 columns = 3,
 variant = "card",
}: FeatureGridProps) {
 const shouldAnimateCards = features.length <= 8;

 return (
 <section className="relative overflow-hidden py-16 lg:py-20">
 <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_15%_20%,rgba(14,165,233,0.1),transparent_35%),radial-gradient(circle_at_85%_80%,rgba(249,115,22,0.08),transparent_33%)]" />
 <div className="relative">
 {(title || subtitle) && (
 <AnimatedSection className="mb-12">
 {title && <h2 className="text-3xl font-bold text-foreground">{title}</h2>}
 {subtitle && <p className="mt-3 text-lg text-muted-foreground">{subtitle}</p>}
 </AnimatedSection>
 )}
 <div className={`grid gap-6 ${gridCols[columns]}`} style={{ contentVisibility: "auto", containIntrinsicSize: "1200px" }}>
 {features.map((feature, i) => {
  const Icon = getContentIcon(feature.title);
  const summary = getFeatureFallbackDescription(feature.title, feature.description);
  const tag = getFeatureTag(feature.title);
  return variant === "card" ? (
  <div key={i} className={shouldAnimateCards ? "reveal in" : ""}>
    <div className="group relative h-full overflow-hidden rounded-3xl border border-border/70 bg-card/95 p-6 shadow-sm transition-colors duration-300 hover:border-primary/25">
   <div className="relative h-full">
    <div className="mb-5 flex items-center justify-between gap-3">
     <div className="relative flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-primary/20 to-primary/5 transition-colors duration-300 group-hover:from-primary/30 group-hover:to-primary/10 service-accent-soft service-accent-border border border-transparent">
    <Icon className="w-6 h-6 text-primary" />
    <div className="absolute inset-0 rounded-xl bg-primary/5 blur-lg opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
    </div>
    <span className="rounded-full border border-primary/20 bg-primary/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-primary">
      {tag}
    </span>
    </div>
    <h3 className="mb-2 text-lg font-semibold text-foreground">{feature.title}</h3>
    <p className="text-sm leading-7 text-muted-foreground">{summary}</p>
    <div className="mt-5 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
      Conversion-ready module
    </div>
  </div>
  </div>
  </div>
  ) : variant === "icon" ? (
  <div key={i} className="flex gap-4 group">
     <div className="relative flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-primary/15 to-primary/5 transition-colors duration-300 group-hover:from-primary/25 group-hover:to-primary/10 service-accent-soft service-accent-border border border-transparent">
   <Icon className="w-5 h-5 text-primary" />
   <div className="absolute inset-0 rounded-xl bg-primary/5 blur-lg opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
   </div>
   <div>
   <h3 className="font-semibold text-foreground mb-1">{feature.title}</h3>
  <p className="text-sm text-muted-foreground leading-relaxed">{summary}</p>
   </div>
  </div>
  ) : (
  <div key={i} className="group">
   <div className="flex items-start gap-3">
     <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 transition-colors duration-300 group-hover:bg-primary/20 service-accent-soft service-accent-border border border-transparent">
    <Icon className="w-4 h-4 text-primary" />
   </div>
   <div>
    <h3 className="font-semibold text-foreground mb-1">{feature.title}</h3>
    <p className="text-sm text-muted-foreground leading-relaxed">{summary}</p>
   </div>
   </div>
    </div>
  );
 })}
   </div>
 </div>
 </section>
 );
}
